import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PHOTO_METADATA_NOTICE, scanPost } from '@/lib/locationLint';

/**
 * LocationLintDialog
 * Runs the location-safe lint over the given fields, and asks the owner to
 * confirm before publishing.
 *
 * Usage:
 *   const [pending, setPending] = useState(false);
 *   <LocationLintDialog open={pending} fields={...} onCancel={...} onConfirm={...} />
 */
export const LocationLintDialog = ({ open, onCancel, onConfirm, fields, publishing = true }) => {
  const issues = useMemo(() => scanPost(fields || []), [fields]);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) setAcknowledged(false);
  }, [open]);

  const hasIssues = issues.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-lg" data-testid="location-lint-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-[hsl(var(--destructive))]" />
            {hasIssues ? 'Location safety review' : 'Ready to publish?'}
          </DialogTitle>
          <DialogDescription>
            {hasIssues
              ? 'We found phrases that may reveal a location. This is only a warning \u2014 you can still publish if you meant to include them.'
              : 'No obvious location indicators found in the text. Please double-check images too.'}
          </DialogDescription>
        </DialogHeader>

        {hasIssues && (
          <ul className="max-h-64 overflow-y-auto space-y-2 text-sm" data-testid="location-lint-issues">
            {issues.map((i) => (
              <li key={i.id} className="rounded-lg border border-[hsl(var(--destructive))]/25 bg-[hsl(var(--destructive))]/5 p-3">
                <p className="font-medium text-[hsl(var(--foreground))]">{i.label}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">In {i.source}. {i.reason}</p>
                <p className="mt-1.5 font-mono text-xs bg-[hsl(var(--muted))] px-2 py-1 rounded inline-block max-w-full break-all">
                  “{i.match}”
                </p>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-start gap-2 rounded-lg border border-[hsl(45_90%_50%/0.35)] bg-[hsl(45_90%_50%/0.08)] px-3 py-2 text-xs text-[hsl(38_65%_28%)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{PHOTO_METADATA_NOTICE}</span>
        </div>

        {hasIssues && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1"
              data-testid="location-lint-acknowledge"
            />
            <span>I have reviewed the highlighted text and any photos, and I still want to publish.</span>
          </label>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel} data-testid="location-lint-cancel">
            Back to editing
          </Button>
          <Button
            onClick={onConfirm}
            disabled={hasIssues && !acknowledged}
            data-testid="location-lint-confirm"
          >
            {publishing ? 'Publish anyway' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
